import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { usePmsTheme } from "./PmsThemeContext";

const DIALOG_CSS = `
.pcd-overlay{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(4,9,18,.72);backdrop-filter:blur(8px);z-index:3200}
.pcd-card{width:min(460px,100%);border-radius:22px;border:1px solid transparent;background:linear-gradient(180deg,rgba(18,24,39,.98),rgba(7,12,22,.98)) padding-box,linear-gradient(135deg,rgba(48,216,255,.5),rgba(168,85,247,.45),rgba(255,120,75,.45)) border-box;box-shadow:0 32px 80px rgba(0,0,0,.55);color:#eef4ff;overflow:hidden}
.pcd-card.pcd-light{background:linear-gradient(180deg,rgba(255,255,255,.99),rgba(246,240,232,.98)) padding-box,linear-gradient(135deg,rgba(33,65,80,.16),rgba(156,115,67,.22),rgba(246,139,61,.2)) border-box;color:#17303f;box-shadow:0 26px 70px rgba(31,41,55,.18)}
.pcd-head{padding:22px 24px 12px;border-bottom:1px solid rgba(255,255,255,.08)}
.pcd-light .pcd-head{border-bottom-color:rgba(23,48,63,.08)}
.pcd-eyebrow{font-size:.72rem;letter-spacing:.24em;text-transform:uppercase;color:rgba(203,213,225,.74);font-weight:800}
.pcd-light .pcd-eyebrow{color:rgba(79,99,115,.72)}
.pcd-title{margin:10px 0 0;font-size:1.2rem;line-height:1.15;font-weight:800}
.pcd-body{padding:18px 24px 22px;display:grid;gap:12px}
.pcd-message{margin:0;font-size:.95rem;line-height:1.6;color:rgba(226,232,240,.82)}
.pcd-light .pcd-message{color:rgba(71,85,105,.84)}
.pcd-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;padding:0 24px 24px}
.pcd-btn{border:none;border-radius:14px;padding:10px 16px;font-size:.88rem;font-weight:800;cursor:pointer;transition:transform .18s ease,opacity .18s ease,box-shadow .18s ease}
.pcd-btn:hover:not(:disabled){transform:translateY(-1px)}
.pcd-btn:disabled{opacity:.62;cursor:wait;transform:none;box-shadow:none}
.pcd-btn--ghost{background:rgba(255,255,255,.06);color:#e5edf9;border:1px solid rgba(255,255,255,.1)}
.pcd-light .pcd-btn--ghost{background:rgba(23,48,63,.05);color:#17303f;border:1px solid rgba(23,48,63,.08)}
.pcd-btn--confirm{color:#fff;box-shadow:0 16px 30px rgba(245,158,11,.18)}
.pcd-btn--warning{background:linear-gradient(135deg,#f59e0b 0%,#dc2626 100%)}
.pcd-btn--primary{background:linear-gradient(135deg,#1d4ed8 0%,#06b6d4 100%)}
@media (max-width:640px){
  .pcd-overlay{padding:16px}
  .pcd-card{border-radius:18px}
  .pcd-head,.pcd-body,.pcd-actions{padding-left:18px;padding-right:18px}
  .pcd-actions{justify-content:stretch}
  .pcd-btn{flex:1 1 100%}
}
@media (max-height:820px){.pcd-card{zoom:0.9}}
@media (max-height:720px){.pcd-card{zoom:0.8}}
@media (max-height:620px){.pcd-card{zoom:0.7}}
`;

export default function PmsConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onClose,
  isBusy = false,
  tone = "warning",
  eyebrow = "Pilcore",
  showCancel = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  confirmDisabled = false,
  zIndex = 3200,
}) {
  const { themeId } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    const dialog = dialogRef.current;
    const focusableElements = () => Array.from(dialog.querySelectorAll(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    ));
    const focusFirst = () => (focusableElements()[0] || dialog).focus();
    focusFirst();

    const keepFocusInDialog = (event) => {
      if (!dialog.contains(event.target)) focusFirst();
    };
    const trapTab = (event) => {
      if (event.key !== "Tab") return;
      const elements = focusableElements();
      const first = elements[0] || dialog;
      const last = elements[elements.length - 1] || dialog;
      if (!dialog.contains(document.activeElement) || document.activeElement === dialog) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("focusin", keepFocusInDialog);
    document.addEventListener("keydown", trapTab);
    return () => {
      document.removeEventListener("focusin", keepFocusInDialog);
      document.removeEventListener("keydown", trapTab);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isBusy && closeOnEscape) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [closeOnEscape, isBusy, isOpen, onClose]);

  if (!isOpen) return null;

  const content = (
    <>
      <style>{DIALOG_CSS}</style>
      <div
        className="pcd-overlay"
        style={{ zIndex }}
        onClick={() => {
          if (!isBusy && closeOnOverlayClick) onClose?.();
        }}
      >
        <div
          className={`pcd-card ${isLightTheme ? "pcd-light" : ""}`}
          role="dialog"
          ref={dialogRef}
          tabIndex={-1}
          aria-modal="true"
          aria-label={title || "Confirmation dialog"}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="pcd-head">
            <div className="pcd-eyebrow">{eyebrow}</div>
            <h2 className="pcd-title">{title}</h2>
          </div>

          <div className="pcd-body">
            {typeof message === "string" ? <p className="pcd-message">{message}</p> : message}
          </div>

          <div className="pcd-actions">
            {showCancel && (
              <button
                type="button"
                className="pcd-btn pcd-btn--ghost"
                onClick={onClose}
                disabled={isBusy}
              >
                {cancelLabel}
              </button>
            )}

            <button
              type="button"
              className={`pcd-btn pcd-btn--confirm ${tone === "primary" ? "pcd-btn--primary" : "pcd-btn--warning"}`}
              onClick={onConfirm}
              disabled={isBusy || confirmDisabled}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}
